import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  alpha,
  Tooltip,
  IconButton,
  Badge
} from '@mui/material';
import {
  QrCode as QrCodeIcon,
  HelpOutline as HelpOutlineIcon,
  Storage as StorageIcon
} from '@mui/icons-material';
import EditableElement from './EditableElement.js';

const TicketPreview = ({ configuracoes, handleChange, customTheme }) => {
  const dataAtual = new Date();
  const dataFormatada = dataAtual.toLocaleDateString('pt-BR');
  const horaFormatada = dataAtual.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  
  // Estados para armazenar estilos personalizados de cada elemento
  const [elementStyles, setElementStyles] = useState({
    companyName: {},
    companyAddress: {},
    companyPhone: {},
    dateTime: {},
    serviceType: {},
    preferential: {},
    ticketLabel: {},
    ticketPrefix: {},
    ticketNumber: {},
    instructions: {},
    customMessage: {},
    qrCode: {},
    siteUrl: {}
  });

  // Flag para prevenir loops infinitos nas atualizações
  const updatingFromTemplate = useRef(false);

  // Função para atualizar o estilo de um elemento específico
  const updateElementStyle = (elementId, newStyle) => {
    setElementStyles(prev => {
      const updatedStyles = {
        ...prev,
        [elementId]: newStyle
      };
      
      // Atualizar o template HTML apenas se não estiver sincronizando do template
      if (!updatingFromTemplate.current) {
        updateTemplateWithStyles(updatedStyles);
      }
      
      return updatedStyles;
    });
  };

  // Função para atualizar o template HTML com base nos estilos 
  const updateTemplateWithStyles = (styles) => {
    // Começar com o template atual ou usar o padrão
    let templateHtml = configuracoes.templateTicket;
    
    if (!templateHtml) return; // Se não houver template, não fazer nada
    
    // Atualizar o estilo do nome da empresa
    if (styles.companyName) {
      let fontSize = "20px"; // Tamanho padrão
      let fontWeight = "bold"; // Peso padrão
      let fontStyle = "normal"; // Estilo padrão
      let marginBottom = "5px"; // Margem padrão
      let textDecoration = "none"; // Decoração padrão
      
      // Mapear os estilos categóricos para valores CSS
      if (styles.companyName.fontSize === 'small') fontSize = "16px";
      else if (styles.companyName.fontSize === 'medium') fontSize = "20px";
      else if (styles.companyName.fontSize === 'large') fontSize = "24px";
      
      if (styles.companyName.fontWeight === 'bold') fontWeight = "bold";
      else if (styles.companyName.fontWeight === 'normal') fontWeight = "normal";
      
      if (styles.companyName.fontStyle === 'italic') fontStyle = "italic";
      else if (styles.companyName.fontStyle === 'normal') fontStyle = "normal";
      
      if (styles.companyName.textDecoration === 'underline') textDecoration = "underline";
      else if (styles.companyName.textDecoration === 'none') textDecoration = "none";
      
      if (styles.companyName.margin === 'small') marginBottom = "3px";
      else if (styles.companyName.margin === 'medium') marginBottom = "5px";
      else if (styles.companyName.margin === 'large') marginBottom = "10px";
      
      // Substituir o estilo da tag h1 do nome da empresa
      templateHtml = templateHtml.replace(
        /<h1 style="[^"]*">/i,
        `<h1 style="font-size: ${fontSize}; font-weight: ${fontWeight}; font-style: ${fontStyle}; text-decoration: ${textDecoration}; text-transform: uppercase; margin: ${marginBottom} 0;">`
      );
    }
    
    // Atualizar o estilo do endereço
    if (styles.companyAddress) {
      let fontSize = "12px"; // Tamanho padrão
      let fontWeight = "normal"; // Peso padrão
      let fontStyle = "normal"; // Estilo padrão
      let marginBottom = "3px"; // Margem padrão
      let textDecoration = "none"; // Decoração padrão
      
      if (styles.companyAddress.fontSize === 'small') fontSize = "10px";
      else if (styles.companyAddress.fontSize === 'medium') fontSize = "12px";
      else if (styles.companyAddress.fontSize === 'large') fontSize = "14px";
      
      if (styles.companyAddress.fontWeight === 'bold') fontWeight = "bold";
      else if (styles.companyAddress.fontWeight === 'normal') fontWeight = "normal";
      
      if (styles.companyAddress.fontStyle === 'italic') fontStyle = "italic";
      else if (styles.companyAddress.fontStyle === 'normal') fontStyle = "normal";
      
      if (styles.companyAddress.textDecoration === 'underline') textDecoration = "underline";
      else if (styles.companyAddress.textDecoration === 'none') textDecoration = "none";
      
      if (styles.companyAddress.margin === 'small') marginBottom = "2px";
      else if (styles.companyAddress.margin === 'medium') marginBottom = "3px";
      else if (styles.companyAddress.margin === 'large') marginBottom = "6px";
      
      // Substituir o estilo do parágrafo do endereço
      templateHtml = templateHtml.replace(
        /<p style="font-size: 12px; margin: 3px 0;">\{\{enderecoEmpresa\}\}<\/p>/,
        `<p style="font-size: ${fontSize}; font-weight: ${fontWeight}; font-style: ${fontStyle}; text-decoration: ${textDecoration}; margin: ${marginBottom} 0;">{{enderecoEmpresa}}</p>`
      );
    }
    
    // Atualizar o estilo do telefone
    if (styles.companyPhone) {
      let fontSize = "12px";
      let fontWeight = "normal";
      let fontStyle = "normal";
      let marginBottom = "3px";
      let textDecoration = "none";
      
      if (styles.companyPhone.fontSize === 'small') fontSize = "10px";
      else if (styles.companyPhone.fontSize === 'medium') fontSize = "12px";
      else if (styles.companyPhone.fontSize === 'large') fontSize = "14px";
      
      if (styles.companyPhone.fontWeight === 'bold') fontWeight = "bold";
      else if (styles.companyPhone.fontWeight === 'normal') fontWeight = "normal";
      
      if (styles.companyPhone.fontStyle === 'italic') fontStyle = "italic";
      else if (styles.companyPhone.fontStyle === 'normal') fontStyle = "normal";
      
      if (styles.companyPhone.textDecoration === 'underline') textDecoration = "underline";
      else if (styles.companyPhone.textDecoration === 'none') textDecoration = "none";
      
      if (styles.companyPhone.margin === 'small') marginBottom = "2px";
      else if (styles.companyPhone.margin === 'medium') marginBottom = "3px";
      else if (styles.companyPhone.margin === 'large') marginBottom = "6px";
      
      // Substituir o estilo do parágrafo do telefone
      templateHtml = templateHtml.replace(
        /<p style="font-size: 12px; margin: 3px 0;">\{\{telefoneEmpresa\}\}<\/p>/,
        `<p style="font-size: ${fontSize}; font-weight: ${fontWeight}; font-style: ${fontStyle}; text-decoration: ${textDecoration}; margin: ${marginBottom} 0;">{{telefoneEmpresa}}</p>`
      );
    }
    
    // Atualizar o estilo da data/hora
    if (styles.dateTime) {
      let fontSize = "12px";
      let fontWeight = "normal";
      let fontStyle = "normal";
      let marginBottom = "3px";
      let textDecoration = "none";
      
      if (styles.dateTime.fontSize === 'small') fontSize = "10px";
      else if (styles.dateTime.fontSize === 'medium') fontSize = "12px";
      else if (styles.dateTime.fontSize === 'large') fontSize = "14px";
      
      if (styles.dateTime.fontWeight === 'bold') fontWeight = "bold";
      else if (styles.dateTime.fontWeight === 'normal') fontWeight = "normal";
      
      if (styles.dateTime.fontStyle === 'italic') fontStyle = "italic";
      else if (styles.dateTime.fontStyle === 'normal') fontStyle = "normal";
      
      if (styles.dateTime.textDecoration === 'underline') textDecoration = "underline";
      else if (styles.dateTime.textDecoration === 'none') textDecoration = "none";
      
      if (styles.dateTime.margin === 'small') marginBottom = "2px";
      else if (styles.dateTime.margin === 'medium') marginBottom = "3px";
      else if (styles.dateTime.margin === 'large') marginBottom = "6px";
      
      // Substituir o estilo dos parágrafos de data e hora
      templateHtml = templateHtml.replace(
        /<p style="font-size: 12px; margin: 3px 0;">Data: \{\{data\}\}<\/p>/,
        `<p style="font-size: ${fontSize}; font-weight: ${fontWeight}; font-style: ${fontStyle}; text-decoration: ${textDecoration}; margin: ${marginBottom} 0;">Data: {{data}}</p>`
      );
      
      templateHtml = templateHtml.replace(
        /<p style="font-size: 12px; margin: 3px 0;">Hora: \{\{hora\}\}<\/p>/,
        `<p style="font-size: ${fontSize}; font-weight: ${fontWeight}; font-style: ${fontStyle}; text-decoration: ${textDecoration}; margin: ${marginBottom} 0;">Hora: {{hora}}</p>`
      );
    }
    
    // Atualizar o estilo do tipo de serviço
    if (styles.serviceType) {
      let fontSize = "16px";
      let fontWeight = "bold";
      let fontStyle = "normal";
      let marginBottom = "8px";
      let textDecoration = "none";
      
      if (styles.serviceType.fontSize === 'small') fontSize = "14px";
      else if (styles.serviceType.fontSize === 'medium') fontSize = "16px";
      else if (styles.serviceType.fontSize === 'large') fontSize = "18px";
      
      if (styles.serviceType.fontWeight === 'bold') fontWeight = "bold";
      else if (styles.serviceType.fontWeight === 'normal') fontWeight = "normal";
      
      if (styles.serviceType.fontStyle === 'italic') fontStyle = "italic";
      else if (styles.serviceType.fontStyle === 'normal') fontStyle = "normal";
      
      if (styles.serviceType.textDecoration === 'underline') textDecoration = "underline";
      else if (styles.serviceType.textDecoration === 'none') textDecoration = "none";
      
      if (styles.serviceType.margin === 'small') marginBottom = "4px";
      else if (styles.serviceType.margin === 'medium') marginBottom = "8px";
      else if (styles.serviceType.margin === 'large') marginBottom = "12px";
      
      // Substituir o estilo da tag h2 do tipo de serviço
      templateHtml = templateHtml.replace(
        /<h2 style="[^"]*">/i,
        `<h2 style="font-size: ${fontSize}; font-weight: ${fontWeight}; font-style: ${fontStyle}; text-decoration: ${textDecoration}; text-transform: uppercase; margin: ${marginBottom} 0;">`
      );
    }
    
    // Atualizar o estilo do número da senha
    if (styles.ticketNumber) {
      let fontSize = "48px";
      let fontWeight = "bold";
      let fontStyle = "normal";
      let marginBottom = "10px";
      let textDecoration = "none";
      
      if (styles.ticketNumber.fontSize === 'small') fontSize = "36px";
      else if (styles.ticketNumber.fontSize === 'medium') fontSize = "48px";
      else if (styles.ticketNumber.fontSize === 'large') fontSize = "60px";
      
      if (styles.ticketNumber.fontWeight === 'bold') fontWeight = "bold";
      else if (styles.ticketNumber.fontWeight === 'normal') fontWeight = "normal";
      
      if (styles.ticketNumber.fontStyle === 'italic') fontStyle = "italic";
      else if (styles.ticketNumber.fontStyle === 'normal') fontStyle = "normal";
      
      if (styles.ticketNumber.textDecoration === 'underline') textDecoration = "underline";
      else if (styles.ticketNumber.textDecoration === 'none') textDecoration = "none";
      
      if (styles.ticketNumber.margin === 'small') marginBottom = "5px";
      else if (styles.ticketNumber.margin === 'medium') marginBottom = "10px";
      else if (styles.ticketNumber.margin === 'large') marginBottom = "15px";
      
      // Substituir o estilo da tag h1 da senha
      templateHtml = templateHtml.replace(
        /<h1 style="font-size: 48px; font-weight: bold; margin: 10px 0;">\{\{senha\}\}<\/h1>/,
        `<h1 style="font-size: ${fontSize}; font-weight: ${fontWeight}; font-style: ${fontStyle}; text-decoration: ${textDecoration}; margin: ${marginBottom} 0;">{{senha}}</h1>`
      );
    }
    
    // Atualizar o estilo das instruções
    if (styles.instructions) {
      let fontSize = "12px";
      let fontWeight = "normal";
      let fontStyle = "normal";
      let marginBottom = "3px";
      let textDecoration = "none";
      
      if (styles.instructions.fontSize === 'small') fontSize = "10px";
      else if (styles.instructions.fontSize === 'medium') fontSize = "12px";
      else if (styles.instructions.fontSize === 'large') fontSize = "14px";
      
      if (styles.instructions.fontWeight === 'bold') fontWeight = "bold";
      else if (styles.instructions.fontWeight === 'normal') fontWeight = "normal";
      
      if (styles.instructions.fontStyle === 'italic') fontStyle = "italic";
      else if (styles.instructions.fontStyle === 'normal') fontStyle = "normal";
      
      if (styles.instructions.textDecoration === 'underline') textDecoration = "underline";
      else if (styles.instructions.textDecoration === 'none') textDecoration = "none";
      
      if (styles.instructions.margin === 'small') marginBottom = "2px";
      else if (styles.instructions.margin === 'medium') marginBottom = "3px";
      else if (styles.instructions.margin === 'large') marginBottom = "6px";
      
      // Substituir o estilo das instruções
      templateHtml = templateHtml.replace(
        /<p style="font-size: 12px; margin: 3px 0;">Aguarde o chamado da sua senha<\/p>/,
        `<p style="font-size: ${fontSize}; font-weight: ${fontWeight}; font-style: ${fontStyle}; text-decoration: ${textDecoration}; margin: ${marginBottom} 0;">Aguarde o chamado da sua senha</p>`
      );
      
      templateHtml = templateHtml.replace(
        /<p style="font-size: 12px; margin: 3px 0;">no painel eletrônico<\/p>/,
        `<p style="font-size: ${fontSize}; font-weight: ${fontWeight}; font-style: ${fontStyle}; text-decoration: ${textDecoration}; margin: ${marginBottom} 0;">no painel eletrônico</p>`
      );
    }
    
    // Atualizar o estilo da mensagem extra
    if (styles.customMessage) {
      let fontSize = "12px";
      let fontWeight = "normal";
      let fontStyle = "italic";
      let marginBottom = "8px";
      let textDecoration = "none";
      
      if (styles.customMessage.fontSize === 'small') fontSize = "10px";
      else if (styles.customMessage.fontSize === 'medium') fontSize = "12px";
      else if (styles.customMessage.fontSize === 'large') fontSize = "14px";
      
      if (styles.customMessage.fontWeight === 'bold') fontWeight = "bold";
      else if (styles.customMessage.fontWeight === 'normal') fontWeight = "normal";
      
      if (styles.customMessage.fontStyle === 'italic') fontStyle = "italic";
      else if (styles.customMessage.fontStyle === 'normal') fontStyle = "normal";
      
      if (styles.customMessage.textDecoration === 'underline') textDecoration = "underline";
      else if (styles.customMessage.textDecoration === 'none') textDecoration = "none";
      
      if (styles.customMessage.margin === 'small') marginBottom = "4px";
      else if (styles.customMessage.margin === 'medium') marginBottom = "8px";
      else if (styles.customMessage.margin === 'large') marginBottom = "12px";
      
      // Substituir o estilo da mensagem extra
      templateHtml = templateHtml.replace(
        /<p style="font-style: italic; font-size: 12px; margin: 8px 0;">\{\{mensagemExtra\}\}<\/p>/,
        `<p style="font-size: ${fontSize}; font-weight: ${fontWeight}; font-style: ${fontStyle}; text-decoration: ${textDecoration}; margin: ${marginBottom} 0;">{{mensagemExtra}}</p>`
      );
    }
    
    // Atualizar o estilo do QR Code
    if (styles.qrCode) {
      let fontSize = "11px";
      let fontWeight = "normal";
      let fontStyle = "normal";
      let marginBottom = "3px";
      let textDecoration = "none";
      
      if (styles.qrCode.fontSize === 'small') fontSize = "9px";
      else if (styles.qrCode.fontSize === 'medium') fontSize = "11px";
      else if (styles.qrCode.fontSize === 'large') fontSize = "13px";
      
      if (styles.qrCode.fontWeight === 'bold') fontWeight = "bold";
      else if (styles.qrCode.fontWeight === 'normal') fontWeight = "normal";
      
      if (styles.qrCode.fontStyle === 'italic') fontStyle = "italic";
      else if (styles.qrCode.fontStyle === 'normal') fontStyle = "normal";
      
      if (styles.qrCode.textDecoration === 'underline') textDecoration = "underline";
      else if (styles.qrCode.textDecoration === 'none') textDecoration = "none";
      
      if (styles.qrCode.margin === 'small') marginBottom = "2px";
      else if (styles.qrCode.margin === 'medium') marginBottom = "3px";
      else if (styles.qrCode.margin === 'large') marginBottom = "6px";
      
      // Substituir os estilos dos textos do QR Code
      templateHtml = templateHtml.replace(
        /<p style="font-size: 11px; margin: 3px 0;">Escaneie o QR Code<\/p>/,
        `<p style="font-size: ${fontSize}; font-weight: ${fontWeight}; font-style: ${fontStyle}; text-decoration: ${textDecoration}; margin: ${marginBottom} 0;">Escaneie o QR Code</p>`
      );
      
      templateHtml = templateHtml.replace(
        /<p style="font-size: 11px; margin: 3px 0;">para acessar nosso site<\/p>/,
        `<p style="font-size: ${fontSize}; font-weight: ${fontWeight}; font-style: ${fontStyle}; text-decoration: ${textDecoration}; margin: ${marginBottom} 0;">para acessar nosso site</p>`
      );
    }
    
    // Atualizar o estilo do site da empresa
    if (styles.siteUrl) {
      let fontSize = "11px";
      let fontWeight = "normal";
      let fontStyle = "normal";
      let marginBottom = "5px";
      let textDecoration = "none";
      
      if (styles.siteUrl.fontSize === 'small') fontSize = "9px";
      else if (styles.siteUrl.fontSize === 'medium') fontSize = "11px";
      else if (styles.siteUrl.fontSize === 'large') fontSize = "13px";
      
      if (styles.siteUrl.fontWeight === 'bold') fontWeight = "bold";
      else if (styles.siteUrl.fontWeight === 'normal') fontWeight = "normal";
      
      if (styles.siteUrl.fontStyle === 'italic') fontStyle = "italic";
      else if (styles.siteUrl.fontStyle === 'normal') fontStyle = "normal";
      
      if (styles.siteUrl.textDecoration === 'underline') textDecoration = "underline";
      else if (styles.siteUrl.textDecoration === 'none') textDecoration = "none";
      
      if (styles.siteUrl.margin === 'small') marginBottom = "3px";
      else if (styles.siteUrl.margin === 'medium') marginBottom = "5px";
      else if (styles.siteUrl.margin === 'large') marginBottom = "8px";
      
      // Substituir o estilo do site da empresa
      templateHtml = templateHtml.replace(
        /<p style="font-size: 11px; margin: 5px 0;">\{\{siteEmpresa\}\}<\/p>/,
        `<p style="font-size: ${fontSize}; font-weight: ${fontWeight}; font-style: ${fontStyle}; text-decoration: ${textDecoration}; margin: ${marginBottom} 0;">{{siteEmpresa}}</p>`
      );
    }
    
    // Atualizar o template nas configurações globais
    if (handleChange && templateHtml !== configuracoes.templateTicket) {
      console.log('Atualizando template HTML com os novos estilos...');
      handleChange({
        target: {
          name: 'templateTicket',
          value: templateHtml
        }
      });
    }
  };

  // UseEffect para sincronizar estilos quando o template muda
  useEffect(() => {
    console.log('Template HTML atualizado, sincronizando estilos...');
    
    // Evitar loops quando o update vier do próprio componente
    if (updatingFromTemplate.current) {
      updatingFromTemplate.current = false;
      return;
    }
    
    // Analisar o template atual para extrair estilos
    if (configuracoes.templateTicket) {
      try {
        updatingFromTemplate.current = true;
        const tempStyles = { ...elementStyles };
        
        // Extrair estilo do nome da empresa
        const companyNameMatch = configuracoes.templateTicket.match(/<h1 style="([^"]*)">/i);
        if (companyNameMatch && companyNameMatch[1]) {
          const style = companyNameMatch[1];
          const fontSize = style.match(/font-size:\s*(\d+)px/);
          const fontWeight = style.match(/font-weight:\s*(\w+)/);
          const fontStyle = style.match(/font-style:\s*(\w+)/);
          const margin = style.match(/margin:\s*(\d+)px/);
          const textDecoration = style.match(/text-decoration:\s*(\w+)/);
          
          // Converter para categorias usadas no editor visual
          if (fontSize) {
            const size = parseInt(fontSize[1]);
            if (size <= 16) tempStyles.companyName.fontSize = 'small';
            else if (size <= 20) tempStyles.companyName.fontSize = 'medium';
            else tempStyles.companyName.fontSize = 'large';
          }
          
          if (fontWeight) tempStyles.companyName.fontWeight = fontWeight[1];
          if (fontStyle) tempStyles.companyName.fontStyle = fontStyle[1];
          if (textDecoration) tempStyles.companyName.textDecoration = textDecoration[1];
          
          if (margin) {
            const marginSize = parseInt(margin[1]);
            if (marginSize <= 3) tempStyles.companyName.margin = 'small';
            else if (marginSize <= 5) tempStyles.companyName.margin = 'medium';
            else tempStyles.companyName.margin = 'large';
          }
        }
        
        // Análise similar pode ser feita para outros elementos
        // Para manter o código conciso, deixamos como exemplo apenas o nome da empresa
        // Mais elementos podem ser adicionados seguindo a mesma lógica
        
        // Atualizar os estilos no estado
        setElementStyles(tempStyles);
      } catch (error) {
        console.error('Erro ao analisar template HTML:', error);
      } finally {
        // Aguardar até o próximo ciclo para redefinir a flag
        setTimeout(() => {
          updatingFromTemplate.current = false;
        }, 0);
      }
    }
  }, [configuracoes.templateTicket]);

  // Aplicar estilos personalizados com base nos valores definidos
  const applyCustomStyles = (elementId, defaultStyles = {}) => {
    const customStyle = elementStyles[elementId] || {};
    
    // Transforme os valores categóricos em valores CSS reais
    let styles = { ...defaultStyles };
    
    // Tamanho da fonte
    if (customStyle.fontSize === 'small') styles.fontSize = '0.8em';
    else if (customStyle.fontSize === 'medium') styles.fontSize = '1em';
    else if (customStyle.fontSize === 'large') styles.fontSize = '1.2em';
    
    // Estilo da fonte
    if (customStyle.fontWeight) styles.fontWeight = customStyle.fontWeight;
    if (customStyle.fontStyle) styles.fontStyle = customStyle.fontStyle;
    if (customStyle.textDecoration) styles.textDecoration = customStyle.textDecoration;
    
    // Espaçamento
    if (customStyle.margin === 'small') styles.marginBottom = '0.3em';
    else if (customStyle.margin === 'medium') styles.marginBottom = '0.7em';
    else if (customStyle.margin === 'large') styles.marginBottom = '1.2em';
    
    // Altura da linha
    if (customStyle.lineHeight === 'small') styles.lineHeight = '1';
    else if (customStyle.lineHeight === 'medium') styles.lineHeight = '1.2';
    else if (customStyle.lineHeight === 'large') styles.lineHeight = '1.5';
    
    return styles;
  };

  // Renderizar elemento dinâmico com destaque visual
  const renderDynamicElement = (elementType, elementId, defaultStyles, children) => (
    <EditableElement
      elementType={elementType}
      initialStyle={elementStyles[elementId]}
      onStyleChange={(style) => updateElementStyle(elementId, style)}
      customTheme={customTheme}
    >
      <Tooltip 
        title="Esta é uma variável dinâmica preenchida pelo sistema" 
        placement="top"
      >
        <Box
          sx={{ 
            position: 'relative',
            '&:hover': {
              '& .dynamic-indicator': {
                opacity: 1
              }
            }
          }}
        >
          {children}
          <Box 
            className="dynamic-indicator"
            sx={{ 
              position: 'absolute',
              top: -8,
              right: -8,
              opacity: 0.2,
              transition: 'opacity 0.2s'
            }}
          >
            <StorageIcon 
              fontSize="small" 
              sx={{ 
                color: alpha(customTheme.palette.warning.main, 0.8),
                fontSize: '0.9rem'
              }}
            />
          </Box>
        </Box>
      </Tooltip>
    </EditableElement>
  );
  
  // Template padrão que corresponde ao formato usado no backend
  const renderTemplateDefault = () => (
    <Box 
      sx={{ 
        width: '100%', 
        maxWidth: 300, 
        mx: 'auto',
        border: '1px dashed',
        borderColor: alpha(customTheme.palette.divider, 0.4),
        borderRadius: 1,
        p: 0,
        bgcolor: 'background.paper',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative'
      }}
    >
      <Tooltip 
        title="Clique nos elementos para editar seu estilo" 
        placement="top-start"
        sx={{ position: 'absolute', top: 8, right: 8 }}
      >
        <IconButton size="small">
          <HelpOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      
      {/* Logo (se ativado) */}
      {configuracoes.incluirLogo && (
        <Box sx={{ mb: 2, width: '100%', textAlign: 'center' }}>
          <Paper 
            elevation={0} 
            sx={{ 
              display: 'inline-block',
              bgcolor: alpha(customTheme.palette.primary.main, 0.1),
              p: 1,
              width: '80%',
              height: 40,
              borderRadius: 1
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              [Logotipo]
            </Typography>
          </Paper>
        </Box>
      )}
      
      {/* Nome da empresa em destaque */}
      <EditableElement 
        elementType="nome da empresa" 
        initialStyle={elementStyles.companyName} 
        onStyleChange={(style) => updateElementStyle('companyName', style)}
        customTheme={customTheme}
      >
        <Typography 
          variant="h6" 
          fontWeight="bold" 
          align="center" 
          sx={{ 
            mb: 0.5, 
            fontSize: '1.3rem', 
            lineHeight: 1.2,
            textTransform: 'uppercase',
            ...applyCustomStyles('companyName', { 
              mb: 0.5, 
              fontSize: '1.3rem', 
              lineHeight: 1.2,
              textTransform: 'uppercase',
              fontWeight: 'bold'
            })
          }}
        >
          {configuracoes.nomeEmpresa || 'NOME DA EMPRESA'}
        </Typography>
      </EditableElement>
      
      {/* Endereço */}
      {configuracoes.enderecoEmpresa && (
        <EditableElement 
          elementType="endereço" 
          initialStyle={elementStyles.companyAddress} 
          onStyleChange={(style) => updateElementStyle('companyAddress', style)}
          customTheme={customTheme}
        >
          <Typography 
            variant="caption" 
            align="center" 
            sx={{ 
              mb: 0.5, 
              display: 'block',
              ...applyCustomStyles('companyAddress', { 
                mb: 0.5, 
                display: 'block' 
              })
            }}
          >
            {configuracoes.enderecoEmpresa}
          </Typography>
        </EditableElement>
      )}
      
      {/* Telefone */}
      {configuracoes.telefoneEmpresa && (
        <EditableElement 
          elementType="telefone" 
          initialStyle={elementStyles.companyPhone} 
          onStyleChange={(style) => updateElementStyle('companyPhone', style)}
          customTheme={customTheme}
        >
          <Typography 
            variant="caption" 
            align="center" 
            sx={{ 
              mb: 0.5, 
              display: 'block',
              ...applyCustomStyles('companyPhone', { 
                mb: 0.5, 
                display: 'block' 
              })
            }}
          >
            {configuracoes.telefoneEmpresa}
          </Typography>
        </EditableElement>
      )}
      
      {/* Data e Hora - Variáveis dinâmicas */}
      {(configuracoes.incluirData || configuracoes.incluirHora) && (
        renderDynamicElement(
          "data e hora",
          "dateTime",
          { mb: 1 },
          <Box sx={{ 
            width: '100%', 
            textAlign: 'center', 
            mb: 1,
            ...applyCustomStyles('dateTime', { mb: 1 })
          }}>
            {configuracoes.incluirData && (
              <Typography variant="caption" display="block">
                Data: {dataFormatada}
              </Typography>
            )}
            {configuracoes.incluirHora && (
              <Typography variant="caption" display="block">
                Hora: {horaFormatada}
              </Typography>
            )}
          </Box>
        )
      )}
      
      {/* Linha divisória */}
      <Divider flexItem sx={{ my: 1, borderStyle: 'dashed' }} />
      
      {/* Tipo de serviço - Variável dinâmica */}
      {renderDynamicElement(
        "tipo de serviço",
        "serviceType",
        { mb: 1, fontWeight: 'bold' },
        <Typography 
          variant="body2" 
          fontWeight="bold" 
          align="center" 
          sx={{ 
            mb: 1,
            ...applyCustomStyles('serviceType', { 
              mb: 1,
              fontWeight: 'bold'
            })
          }}
        >
          REGISTRO CIVIL
        </Typography>
      )}
      
      {/* Tag Preferencial */}
      <EditableElement 
        elementType="tag preferencial" 
        initialStyle={elementStyles.preferential} 
        onStyleChange={(style) => updateElementStyle('preferential', style)}
        customTheme={customTheme}
      >
        <Box sx={{ 
          px: 1.5, 
          py: 0.3, 
          bgcolor: 'text.primary',
          color: 'background.paper',
          borderRadius: 0,
          mb: 1,
          fontSize: '0.7rem',
          fontWeight: 'bold',
          width: 'auto',
          ...applyCustomStyles('preferential', { 
            px: 1.5, 
            py: 0.3, 
            bgcolor: 'text.primary',
            color: 'background.paper',
            borderRadius: 0,
            mb: 1,
            fontSize: '0.7rem',
            fontWeight: 'bold'
          })
        }}>
          {configuracoes.textoPreferencial || "ATENDIMENTO PREFERENCIAL"}
        </Box>
      </EditableElement>
      
      {/* Linha divisória */}
      <Divider flexItem sx={{ my: 1, borderStyle: 'dashed' }} />
      
      {/* Texto da senha */}
      <EditableElement 
        elementType="rótulo da senha" 
        initialStyle={elementStyles.ticketLabel} 
        onStyleChange={(style) => updateElementStyle('ticketLabel', style)}
        customTheme={customTheme}
      >
        <Typography 
          variant="body2" 
          fontWeight="bold" 
          align="center"
          sx={{
            ...applyCustomStyles('ticketLabel', { 
              fontWeight: 'bold'
            })
          }}
        >
          {configuracoes.textoSenha || "SENHA"}
        </Typography>
      </EditableElement>
      
      {/* Número da senha - Variável dinâmica */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {/* Prefixo da senha - Variável dinâmica */}
        {renderDynamicElement(
          "prefixo da senha",
          "ticketPrefix",
          { fontWeight: 'bold', fontSize: '3rem' },
          <Typography 
            variant="h2" 
            fontWeight="bold" 
            align="center" 
            sx={{ 
              ...applyCustomStyles('ticketPrefix', { 
                fontWeight: 'bold',
                fontSize: '3rem'
              })
            }}
          >
            A
          </Typography>
        )}
        
        {/* Número - Variável dinâmica */}
        {renderDynamicElement(
          "número da senha",
          "ticketNumber",
          { my: 0.5, fontWeight: 'bold', fontSize: '3rem' },
          <Typography 
            variant="h2" 
            fontWeight="bold" 
            align="center" 
            sx={{ 
              my: 0.5,
              ...applyCustomStyles('ticketNumber', { 
                my: 0.5,
                fontWeight: 'bold',
                fontSize: '3rem'
              })
            }}
          >
            001
          </Typography>
        )}
      </Box>
      
      {/* Linha divisória */}
      <Divider flexItem sx={{ my: 1, borderStyle: 'dashed' }} />
      
      {/* Instruções */}
      <EditableElement 
        elementType="instruções" 
        initialStyle={elementStyles.instructions} 
        onStyleChange={(style) => updateElementStyle('instructions', style)}
        customTheme={customTheme}
      >
        <Box sx={{
          textAlign: 'center',
          ...applyCustomStyles('instructions', {})
        }}>
          <Typography variant="caption" align="center" sx={{ display: 'block' }}>
            {configuracoes.instrucao1 || "Aguarde o chamado da sua senha"}
          </Typography>
          <Typography variant="caption" align="center" sx={{ mb: 0.5, display: 'block' }}>
            {configuracoes.instrucao2 || "no painel eletrônico"}
          </Typography>
        </Box>
      </EditableElement>
      
      {/* Mensagem personalizada */}
      {configuracoes.mensagemExtra && (
        <EditableElement 
          elementType="mensagem personalizada" 
          initialStyle={elementStyles.customMessage} 
          onStyleChange={(style) => updateElementStyle('customMessage', style)}
          customTheme={customTheme}
        >
          <Typography 
            variant="caption" 
            align="center" 
            sx={{ 
              mb: 0.5, 
              fontStyle: 'italic', 
              display: 'block',
              ...applyCustomStyles('customMessage', { 
                mb: 0.5, 
                fontStyle: 'italic', 
                display: 'block' 
              })
            }}
          >
            {configuracoes.mensagemExtra}
          </Typography>
        </EditableElement>
      )}
      
      {/* QR Code (se ativado) */}
      {configuracoes.incluirQrCode && (
        <EditableElement 
          elementType="QR Code" 
          initialStyle={elementStyles.qrCode} 
          onStyleChange={(style) => updateElementStyle('qrCode', style)}
          customTheme={customTheme}
        >
          <Box sx={{ 
            mt: 1, 
            mb: 0.5, 
            textAlign: 'center',
            ...applyCustomStyles('qrCode', { 
              mt: 1, 
              mb: 0.5, 
              textAlign: 'center' 
            })
          }}>
            <Paper 
              sx={{ 
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 80,
                height: 80,
                bgcolor: alpha(customTheme.palette.common.black, 0.05),
                borderRadius: 1
              }}
            >
              <QrCodeIcon sx={{ fontSize: 40, color: customTheme.palette.text.secondary }} />
            </Paper>
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              {configuracoes.textoQrCode1 || "Escaneie o QR Code"}
            </Typography>
            {configuracoes.qrCodeUrl && (
              <Typography variant="caption" display="block" sx={{ mt: 0.2 }}>
                {configuracoes.textoQrCode2 || "para acessar nosso site"}
              </Typography>
            )}
          </Box>
        </EditableElement>
      )}
      
      {/* Linha divisória */}
      <Divider flexItem sx={{ my: 1, borderStyle: 'dashed' }} />
      
      {/* Site */}
      {configuracoes.siteEmpresa && (
        <EditableElement 
          elementType="site da empresa" 
          initialStyle={elementStyles.siteUrl} 
          onStyleChange={(style) => updateElementStyle('siteUrl', style)}
          customTheme={customTheme}
        >
          <Typography 
            variant="caption" 
            align="center" 
            sx={{ 
              display: 'block',
              ...applyCustomStyles('siteUrl', { display: 'block' })
            }}
          >
            {configuracoes.siteEmpresa}
          </Typography>
        </EditableElement>
      )}
      
      {/* Espaço para corte */}
      <Box sx={{ height: 16 }} />
    </Box>
  );
  
  return (
    <Paper elevation={0} sx={{ 
      p: 2, 
      borderRadius: 3,
      height: '100%',
      minHeight: '100%',
      border: '1px solid',
      borderColor: theme => alpha(theme.palette.divider, 0.1),
      bgcolor: 'background.paper',
      display: 'flex',
      flexDirection: 'column'
    }}>
      
      {/* Dica informativa */}
      <Box 
        sx={{
          mb: 1.5,
          px: 2,
          py: 1,
          bgcolor: alpha(customTheme.palette.info.main, 0.1),
          color: customTheme.palette.info.dark,
          borderRadius: 1,
          fontSize: '0.85rem',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <HelpOutlineIcon fontSize="small" sx={{ mr: 1, color: customTheme.palette.info.main }} />
        <Typography variant="body2">
          Clique nos elementos para editar seu estilo. Os elementos com <StorageIcon fontSize="small" sx={{ fontSize: '0.8rem', verticalAlign: 'middle', ml: 0.5, mr: 0.5, color: customTheme.palette.warning.main }} /> são variáveis dinâmicas.
        </Typography>
      </Box>
      
      {/* Preview da senha com base no template */}
      <Box sx={{ 
        display: 'flex', 
        flexGrow: 1,
        alignItems: 'center', 
        justifyContent: 'center',
        overflowY: 'auto',
        py: 2
      }}>
        {renderTemplateDefault()}
      </Box>
    </Paper>
  );
};

export default TicketPreview; 